import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUserProfile } from '../../model/user/useUserProfile';
import { Membership } from '../../model/user/UserProfile';
import { Box, CircularProgress } from '@mui/material';

/**
 * Component that checks if the user's profile has NOT_ACTIVE status
 * and redirects to the user settings page if needed.
 * This component should be used at the application root level.
 */
const AuthRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();

  // Create an account object from the OIDC user data
  const account = user
    ? {
        keycloakId: user.profile.sub,
        email: user.profile.email || '',
        firstname: user.profile.given_name || '',
        name: user.profile.family_name || '',
        username: user.profile.preferred_username || '',
        member: false,
      }
    : undefined;

  // Use the useUserProfile hook to fetch the user profile
  const { userProfile, isLoading: isProfileLoading } = useUserProfile(account);

  // Check if the user profile has NOT_ACTIVE status and redirect if needed
  useEffect(() => {
    if (!isAuthLoading && !isProfileLoading && isAuthenticated && userProfile) {
      // Skip the check if we're already on the user-settings page
      if (
        window.location.pathname !== '/user-settings' &&
        userProfile.membership === Membership.NOT_ACTIVE
      ) {
        // Redirect to the user settings page to complete the profile
        navigate('/user-settings', {
          state: {
            from: { pathname: window.location.pathname },
            message: 'Bitte vervollständigen Sie Ihr Profil, um fortfahren zu können.',
          },
        });
      }
    }
  }, [isAuthLoading, isProfileLoading, isAuthenticated, userProfile, navigate]);

  // Show loading indicator while checking
  if (isAuthLoading || isProfileLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={100} />
      </Box>
    );
  }

  // Render the children if no redirect is needed
  return <>{children}</>;
};

export default AuthRedirect;
